import { useNavigate } from "react-router-dom";
import { Team } from "../types/team";
import { EntityController } from "./entityController";
import useTeamQuery from "../hooks/query/useTeamQuery";
import { extractReferenceKey } from "../components/forms/fields/basic/EntitySelectionField";
import useTeamLogic from "../hooks/logic/teamLogic";

export interface TeamController extends EntityController<Team> {
  displayName: (data: Team) => string;
  viewTitle: (data: Team) => string;
  editTitle: (data: Team) => string;
}

const useTeamController = (
  id: string | null,
  systemLanguageType: Team["system_language_type"]
): TeamController => {
  const { useFetchById, useCreate, useDeleteById, useUpdate } =
    useTeamQuery(systemLanguageType);
  const { userEnums, metadataFieldNames, defaultTeam, getDisplayName } =
    useTeamLogic(systemLanguageType);
  const fetchById = useFetchById(id || "");
  const update = useUpdate();
  const create = useCreate();
  const deleteById = useDeleteById();
  const navigate = useNavigate();

  const fetchData = async (): Promise<Team> => {
    if (!id) {
      return defaultTeam;
    }
    const res = await fetchById.refetch();
    const teamData = res.data;
    if (!teamData || res.isError) {
      throw new Error("Team data not found");
    }
    return teamData;
  };

  const isCreateMode = id === null;

  const displayName = (data: Team) => getDisplayName(data);

  const viewTitle = (data: Team) => displayName(data);

  const editTitle = (data: Team) =>
    `${isCreateMode ? "Create New" : "Edit"} ${displayName(data)}`;

  const onSubmit = async (data: Partial<Team>) => {
    const updateData: Partial<Team> = {
      internal_notes: data.internal_notes,
      notes: data.notes,
      name: data.name,
      entity_status_of_proofreading: data.entity_status_of_proofreading,
      place_id: extractReferenceKey(data.place),
      place_identification_status: data.place_identification_status,
      revision: data.revision,
      taisho_catalog_no: data.taisho_catalog_no,
      title: data.title,
      version_id: extractReferenceKey(data.version),
      source_language: data.source_language,
      team_contribution_date: data.team_contribution_date,
      attributes_metadata: {
        ...defaultTeam.attributes_metadata,
        ...data.attributes_metadata,
      },
    };

    if (isCreateMode) {
      updateData.system_language_type = systemLanguageType;
    }

    let result;
    if (id) {
      result = await update.mutateAsync({
        id,
        data: { _rev: data._rev, ...updateData },
      });
      if (!result || update.isError) {
        throw new Error("Failed to update Team");
      }
    } else {
      result = await create.mutateAsync({
        ...updateData,
        system_language_type: systemLanguageType,
      });
      if (!result || create.isError) {
        throw new Error("Failed to create Team");
      }
    }
    return result;
  };

  const onDelete = async () => {
    if (!id) {
      throw new Error("Team ID is required for deletion");
    }
    await deleteById.mutateAsync(id);
    if (deleteById.isError) {
      console.error("Error deleting team:", deleteById.error);
      throw new Error("Failed to delete team");
    }
  };

  const deleteWarningMessage = "Are you sure you want to delete this team?";

  const navigateBack = () => navigate(-1);

  const getEditRoute = () => `/teams/${systemLanguageType}/${id}/edit`;

  return {
    fetchData,
    userEnums,

    displayName,
    viewTitle,
    editTitle,
    title: viewTitle,

    defaultValues: defaultTeam,
    metadataFieldNames,
    isCreateMode,
    onSubmit,
    onDelete,
    deleteWarningMessage,
    formCallback: navigateBack,

    getEditRoute,
  };
};

export default useTeamController;
